/**
 * Detects a pdf.js document-load failure (the brochure viewer, story #11626).
 *
 * `ngx-extended-pdf-viewer` reports a failed load twice: once through its
 * `pdfLoadingFailed` output, which the brochure page turns into its own
 * "could not be loaded / Try again" state, and once more as a promise
 * rejection it never catches. Only the first is meant for the app. Left to the
 * global handler, the second replaces that retry state with the full-screen
 * "Something unexpected happened" page, so a missing or broken brochure would
 * look like the whole app crashed.
 *
 * These are the exception names pdf.js gives the failures `getDocument()` can
 * end in: a 404, another bad HTTP status, a file that is not a PDF, a network
 * error, a password prompt that was dismissed, a load that was cancelled.
 * Zone.js can hand the rejection over wrapped, with the original on
 * `rejection`, so both shapes are checked.
 */

const PDF_LOAD_EXCEPTION_NAMES: ReadonlySet<string> = new Set([
  'MissingPDFException',
  'UnexpectedResponseException',
  'InvalidPDFException',
  'UnknownErrorException',
  'PasswordException',
  'AbortException',
]);

export function isPdfDocumentLoadFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const err = error as { name?: unknown; rejection?: unknown };
  const candidate =
    err.rejection && typeof err.rejection === 'object'
      ? (err.rejection as { name?: unknown })
      : err;

  return (
    typeof candidate.name === 'string' &&
    PDF_LOAD_EXCEPTION_NAMES.has(candidate.name)
  );
}
